import { resolveId } from '@medplum/core'
import { featureFlags } from '@seen/core/featureFlags'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { LAB_COPY_KIND_LABELS } from '@seen/core/models/document-references/labCopies'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
  TypographyOverline,
} from '@seen/ui'
import { IconChevronRight, IconFile } from '@tabler/icons-react'
import { orderBy } from 'lodash-es'
import type { JSX } from 'react'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { useFolderAccess } from '#src/components/document-reference/useFolderAccess.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface OtherLabCopiesProps {
  documentReference: ExtendedDocumentReference
}

// The other deliveries of the same Quest result, whichever of them is open.
export function OtherLabCopies({
  documentReference,
}: OtherLabCopiesProps): JSX.Element | null {
  const labCopyGrouping = useFeatureFlag(featureFlags.labCopyGrouping)
  const { hiddenFolderIds } = useFolderAccess()
  const { subject, labCopyGroup } = documentReference
  const enabled = labCopyGrouping && !!subject && !!labCopyGroup
  const { data: members = [] } = useSearch(
    ExtendedDocumentReference,
    enabled
      ? ExtendedDocumentReference.excludingFolders(
          ExtendedDocumentReference.byLabCopyGroup(subject, labCopyGroup),
          hiddenFolderIds
        )
      : undefined,
    { query: { enabled } }
  )
  const others = orderBy(
    members.filter((member) => member.id !== documentReference.id),
    [(member) => member.dateStringWithFallback ?? ''],
    ['asc']
  )
  if (!enabled || others.length === 0) {
    return null
  }

  return (
    <div>
      <TypographyOverline>Other copies ({others.length})</TypographyOverline>
      <ItemGroup>
        {others.map((copy) => (
          <Item key={copy.id} asChild size="sm" className="px-2 py-2">
            <Link
              to={`/Patient/${resolveId(subject)}/documents?documentReferenceId=${copy.id}`}
            >
              <IconFile className="size-4 shrink-0 text-muted-foreground" />
              <ItemContent className="gap-0">
                <ItemTitle>
                  {(copy.labCopyKind &&
                    LAB_COPY_KIND_LABELS[copy.labCopyKind]) ??
                    'Document'}
                </ItemTitle>
                {copy.dateStringWithFallback && (
                  <ItemDescription>
                    Received{' '}
                    <HoverDate
                      date={copy.dateStringWithFallback}
                      format="datetime"
                    />
                  </ItemDescription>
                )}
              </ItemContent>
              <IconChevronRight className="size-4 shrink-0 text-muted-foreground" />
            </Link>
          </Item>
        ))}
      </ItemGroup>
    </div>
  )
}
